import firebase from 'firebase/app';
import React, { useState, useMemo } from 'react';
import DialogContent from '@material-ui/core/DialogContent';
import Tooltip from '@material-ui/core/Tooltip';
import TransformerReadingHelper, { IMeterReadingsView, ITransformerReading } from '../../../../../../@types/model/transformer/reading/transformerReading';
import { Button, DialogActions } from '@material-ui/core';
import MaterialTable from '../../../../../customComponents/materialTable/Table';
import AppFunctionsService from '../../../../../../services/appFunctionsService';
import { Warning } from '@material-ui/icons';
import ConfirmDialog from '../../../../../customComponents/dialog/ConfirmDialog';
import { EmployeeHelper, IUserSession } from '../../../../../../@types/employee';
import lodash from 'lodash';
import ProjectDialog from '../../../../../customComponentV2/dialog/ProjectDialog';

interface ISPUApproveDialogProps {
    open : boolean;
    disapprove ?: boolean;

    onClose : (result : boolean) => void;
    values : Array<IMeterReadingsView>;
    title : string;
    readings : Array<ITransformerReading>;
    session : IUserSession;
}

const SPUApproveDialog = (props : ISPUApproveDialogProps) : React.ReactElement => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);

    const onClose = (result : boolean) : void => {
        if (isLoading) return;
        props.onClose(result);
    };

    const onDialogClose = () : void => {
        if (isLoading) return;
        onClose(false);
    };

    const onCloseClick = () : void => {
        if (isLoading) return;
        onClose(false);
    };

    const save = async () : Promise<void> => {
        setIsLoading(true);

        const session = props.session;

        const disapprove = !!props.disapprove;
        const meterReadings = getFilteredData;
        const guids = lodash.uniq(meterReadings.map(x => x.Guid));
        const readings = props.readings.filter(x => guids.includes(x.GUID));

        meterReadings.forEach((meterReading) => {
            const readingIndex = readings.findIndex(x => x.GUID === meterReading.Guid);
            if (readingIndex < 0) return;

            const reading = readings[readingIndex];
            const meterIndex = reading.TransformerMeterNumberReadings.findIndex(x => x.ItemNumber === meterReading.ItemNumber);
            if (meterIndex < 0) return;


            const update = {
                ...reading,
                TransformerMeterNumberReadings: reading.TransformerMeterNumberReadings.map(z => ({
                    ...z,
                })),
            };

            update.TransformerMeterNumberReadings[meterIndex].Approved = disapprove ? false : true;
            update.TransformerMeterNumberReadings[meterIndex].ApprovedBy = disapprove ? null : EmployeeHelper.doc(session.firebaseUser.uid);
            update.TransformerMeterNumberReadings[meterIndex].ApprovedByName = disapprove ? null : session.firebaseUser.displayName ?? '';
            update.TransformerMeterNumberReadings[meterIndex].ApprovedByEmployee = disapprove ? null : session.employee.EmployeeNumber ?? '';
            update.TransformerMeterNumberReadings[meterIndex].ApprovedOn = disapprove ? null : firebase.firestore.Timestamp.now();

            readings.splice(readingIndex, 1, update);
        });

        await TransformerReadingHelper.batchReadingSave(readings, session);

        setIsLoading(false);
        props.onClose(true);
    };

    const onConfirm = (result : boolean) : void => {
        setShowConfirm(false);
        if (!result) {
            return;
        }
        save();
    };

    const onOkClick = () : void => {
        if (isLoading) return;

        if (props.disapprove) {
            save();
        } else {
            if (props.values.some(n => !!n.Error)) {
                setShowConfirm(true);
            } else {
                save();
            }
        }
    };

    const getFilteredData = useMemo<Array<IMeterReadingsView>>(() => {
        const values = props.values;
        const disapprove = props.disapprove;
        return values.filter(x => !disapprove ? !x.Approved : x.Approved);
    }, [props.values, props.disapprove]);
    
    return (
        <>
            <ProjectDialog
                isOpen={props.open}
                onClose={onDialogClose}
                maxWidth={'md'}
                fullWidth
                title={props.title}
                isLoadingCircular={isLoading}
            >
                <DialogContent>
                    <div className='fdc'>
                        <MaterialTable<IMeterReadingsView>
                            id='valuesGrid'
                            data={getFilteredData}
                            isLoading={isLoading}
                            rowsPerPage={100}
                            columns={[{
                                header: '',
                                field: 'EPNumber',
                                width: 75,
                                renderCell: row => (
                                    <div className=' fdr aic'>
                                        {
                                            !!row.Error &&
                                            <Tooltip title={row.Error}>
                                                <Warning className={'cy pl10'}/>
                                            </Tooltip>
                                        }
                                    </div>
                                ),
                            }, {
                                header: 'Date',
                                field: 'Date',
                                width: 175,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (<span>{AppFunctionsService.formatDateTimeToDateOnly(row.Date)}</span>),
                            }, {
                                header: 'EP Point',
                                field: 'EPNumber',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Area',
                                field: 'Division',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Group',
                                field: 'Group',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Meter',
                                field: 'MeterNumber',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Meter Reading (kWh)',
                                field: 'Reading',
                                width: 200,
                                enableSort: true,
                                enableFilter: true,
                            }, {
                                header: 'Last Reading (kWh)',
                                field: 'Usage',
                                width: 200,
                                enableSort: true,
                                enableFilter: true,
                                renderCell: row => (
                                    <span>
                                        {
                                            row.Rollover ?
                                                row.Reading - row.Usage + Number(new Array(row.NumberOfDigits).fill('9').join('')) :
                                                row.Reading - row.Usage
                                        }
                                    </span>),
                            }, {
                                header: 'Usage',
                                field: 'Usage',
                                width: 145,
                                enableSort: true,
                                enableFilter: true,
                            }]}
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCloseClick} disabled={isLoading} color='primary'>
                        Cancel
                    </Button>
                    <Button type='button' disabled={isLoading} onClick={onOkClick}>
                        <span className='pl10'>SUBMIT</span>
                    </Button>
                </DialogActions>
            </ProjectDialog>
            {
                showConfirm &&
                <ConfirmDialog
                    open
                    onClose={onConfirm}
                    message={'Some readings have errors, are you sure you want to approve them?'}
                    title={'Confirm'} />
            }
        </>
    );
};

export default SPUApproveDialog;
